import React, { Component } from 'react';
import { Card, Form, Button, Alert, Row, Col, Table, Spinner, Badge } from 'react-bootstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { paymentService } from '../../../../services/PaymentService';

// Enregistrement des composants Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    ArcElement
);

class PaymentReports extends Component {
    constructor(props) {
        super(props);

        const now = new Date();
        const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDayThisMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

        this.state = {
            // Date range filters
            startDate: firstDayThisMonth.toISOString().split('T')[0],
            endDate: lastDayThisMonth.toISOString().split('T')[0],
            period: 'daily', // daily, weekly, monthly
            provider: '',
            status: '',

            // Report data
            summaryData: null,
            periodData: [],
            providerData: [],
            statusData: [],
            topCards: [],

            // Loading state
            loading: false,
            error: null
        };
    }

    componentDidMount() {
        this.loadReportData();
    }

    loadReportData = async () => {
        this.setState({ loading: true, error: null });

        try {
            const { startDate, endDate, period, provider, status } = this.state;

            const params = {
                startDate,
                endDate,
                period,
                provider,
                status
            };

            // Load all report data in parallel
            const [summaryResponse, periodResponse, providerResponse, statusResponse, topCardsResponse] = await Promise.all([
                paymentService.getPaymentSummary(params),
                paymentService.getPaymentsByPeriod(params),
                paymentService.getPaymentsByProvider(params),
                paymentService.getPaymentsByStatus(params),
                paymentService.getTopPaymentCards(params)
            ]);

            this.setState({
                summaryData: summaryResponse,
                periodData: periodResponse || [],
                providerData: providerResponse || [],
                statusData: statusResponse || [],
                topCards: topCardsResponse || []
            });

        } catch (error) {
            console.error('Error loading report data:', error);
            this.setState({ error: error.message || 'Erreur de chargement des données' });
        } finally {
            this.setState({ loading: false });
        }
    };

    handleFilterChange = (field, value) => {
        this.setState({ [field]: value });
    };

    applyFilters = () => {
        this.loadReportData();
    };

    exportToCSV = () => {
        const { periodData, startDate, endDate } = this.state;

        if (periodData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const headers = ['Période', 'Nombre de paiements', 'Montant total', 'Paiements réussis', 'Paiements échoués'];
        const csvContent = [
            headers.join(','),
            ...periodData.map(item => [
                item.period,
                item.totalCount,
                item.totalAmount,
                item.successCount,
                item.failedCount
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `rapport_paiements_${startDate}_${endDate}.csv`;
        link.click();
    };

    renderFilters = () => {
        const { startDate, endDate, period, provider, status, loading } = this.state;

        return (
            <Card className="mb-4">
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="fa fa-filter me-2" />
                        Paramètres du rapport
                    </h6>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Date début</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => this.handleFilterChange('startDate', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Date fin</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => this.handleFilterChange('endDate', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Période</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={period}
                                    onChange={(e) => this.handleFilterChange('period', e.target.value)}
                                >
                                    <option value="daily">Journalier</option>
                                    <option value="weekly">Hebdomadaire</option>
                                    <option value="monthly">Mensuel</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Fournisseur</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={provider}
                                    onChange={(e) => this.handleFilterChange('provider', e.target.value)}
                                >
                                    <option value="">Tous</option>
                                    <option value="OrangeMoney">Orange Money</option>
                                    <option value="MtnMoney">MTN Money</option>
                                    <option value="BankTransfer">Virement</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                        <Col md={2}>
                            <Form.Group className="mb-3">
                                <Form.Label>Statut</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={status}
                                    onChange={(e) => this.handleFilterChange('status', e.target.value)}
                                >
                                    <option value="">Tous</option>
                                    <option value="Completed">Terminé</option>
                                    <option value="Pending">En attente</option>
                                    <option value="Failed">Échec</option>
                                </Form.Control>
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="d-flex gap-2">
                        <Button variant="primary" onClick={this.applyFilters} disabled={loading}>
                            <i className="fa fa-sync me-2" />
                            Actualiser
                        </Button>
                        <Button variant="outline-success" onClick={this.exportToCSV}>
                            <i className="fa fa-download me-2" />
                            Exporter CSV
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    renderSummaryCards = () => {
        const { summaryData } = this.state;

        if (!summaryData) return null;

        return (
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="bg-primary text-white">
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h3 className="mb-1">{summaryData.totalPayments.toLocaleString()}</h3>
                                    <p className="mb-0">Total des paiements</p>
                                </div>
                                <div className="align-self-center">
                                    <i className="fa fa-receipt fa-2x opacity-75"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="bg-success text-white">
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h3 className="mb-1">{summaryData.totalAmount.toLocaleString()}</h3>
                                    <p className="mb-0">Montant total (XOF)</p>
                                </div>
                                <div className="align-self-center">
                                    <i className="fa fa-money-bill-wave fa-2x opacity-75"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="bg-info text-white">
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h3 className="mb-1">{summaryData.successfulPayments.toLocaleString()}</h3>
                                    <p className="mb-0">Paiements réussis</p>
                                </div>
                                <div className="align-self-center">
                                    <i className="fa fa-check-circle fa-2x opacity-75"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="bg-warning text-white">
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <div>
                                    <h3 className="mb-1">{((summaryData.successfulPayments / summaryData.totalPayments) * 100).toFixed(1)}%</h3>
                                    <p className="mb-0">Taux de réussite</p>
                                </div>
                                <div className="align-self-center">
                                    <i className="fa fa-chart-line fa-2x opacity-75"></i>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    };

    renderPeriodChart = () => {
        const { periodData } = this.state;

        if (periodData.length === 0) return null;

        const chartData = {
            labels: periodData.map(item => item.period),
            datasets: [
                {
                    label: 'Montant total (XOF)',
                    data: periodData.map(item => item.totalAmount),
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1
                },
                {
                    label: 'Nombre de paiements',
                    data: periodData.map(item => item.totalCount),
                    type: 'line',
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    yAxisID: 'y1'
                }
            ]
        };

        const options = {
            responsive: true,
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                title: {
                    display: true,
                    text: 'Évolution des paiements par période'
                },
                legend: {
                    position: 'top'
                }
            },
            scales: {
                y: {
                    type: 'linear',
                    display: true,
                    position: 'left'
                },
                y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    grid: {
                        drawOnChartArea: false
                    }
                }
            }
        };

        return (
            <Card className="mb-4">
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="fa fa-chart-bar me-2" />
                        Évolution par période
                    </h6>
                </Card.Header>
                <Card.Body>
                    <Bar data={chartData} options={options} />
                </Card.Body>
            </Card>
        );
    };

    renderProviderChart = () => {
        const { providerData } = this.state;

        if (providerData.length === 0) return null;

        const chartData = {
            labels: providerData.map(item => item.provider),
            datasets: [{
                data: providerData.map(item => item.totalAmount),
                backgroundColor: [
                    'rgba(255, 159, 64, 0.6)',
                    'rgba(255, 205, 86, 0.6)',
                    'rgba(75, 192, 192, 0.6)'
                ],
                borderColor: [
                    'rgba(255, 159, 64, 1)',
                    'rgba(255, 205, 86, 1)',
                    'rgba(75, 192, 192, 1)'
                ],
                borderWidth: 1
            }]
        };

        const options = {
            responsive: true,
            plugins: {
                title: {
                    display: true,
                    text: 'Répartition par fournisseur de paiement'
                },
                legend: {
                    position: 'bottom'
                }
            }
        };

        return (
            <Card className="mb-4">
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="fa fa-chart-pie me-2" />
                        Répartition par fournisseur
                    </h6>
                </Card.Header>
                <Card.Body>
                    <div className="row">
                        <div className="col-md-8">
                            <Doughnut data={chartData} options={options} />
                        </div>
                        <div className="col-md-4">
                            <Table size="sm">
                                <tbody>
                                    {providerData.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.provider}</td>
                                            <td className="text-end">{item.totalAmount.toLocaleString()} XOF</td>
                                            <td className="text-end">({item.totalCount} paiements)</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    renderStatusTable = () => {
        const { statusData } = this.state;

        if (statusData.length === 0) return null;

        const getStatusBadge = (status) => {
            const variants = {
                'Completed': 'success',
                'Pending': 'warning',
                'Failed': 'danger',
                'Processing': 'info',
                'Cancelled': 'secondary'
            };

            return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
        };

        return (
            <Card className="mb-4">
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="fa fa-list me-2" />
                        Répartition par statut
                    </h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table className="mb-0">
                        <thead>
                            <tr>
                                <th>Statut</th>
                                <th className="text-end">Nombre</th>
                                <th className="text-end">Montant total</th>
                                <th className="text-end">Pourcentage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {statusData.map((item, index) => {
                                const totalPayments = statusData.reduce((sum, s) => sum + s.totalCount, 0);
                                const percentage = ((item.totalCount / totalPayments) * 100).toFixed(1);

                                return (
                                    <tr key={index}>
                                        <td>{getStatusBadge(item.status)}</td>
                                        <td className="text-end">{item.totalCount.toLocaleString()}</td>
                                        <td className="text-end">{item.totalAmount.toLocaleString()} XOF</td>
                                        <td className="text-end">{percentage}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    renderTopCards = () => {
        const { topCards } = this.state;

        if (topCards.length === 0) return null;

        return (
            <Card className="mb-4">
                <Card.Header>
                    <h6 className="mb-0">
                        <i className="fa fa-star me-2" />
                        Top cartes par montant de paiement
                    </h6>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table className="mb-0">
                        <thead>
                            <tr>
                                <th>Rang</th>
                                <th>Numéro de carte</th>
                                <th>Bénéficiaire</th>
                                <th className="text-end">Montant total</th>
                                <th className="text-end">Nb. paiements</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topCards.map((card, index) => (
                                <tr key={index}>
                                    <td>
                                        <Badge bg={index === 0 ? 'warning' : index === 1 ? 'secondary' : 'light'} text={index > 1 ? 'dark' : 'white'}>
                                            #{index + 1}
                                        </Badge>
                                    </td>
                                    <td>
                                        <div className="fw-medium">{card.cardNumber}</div>
                                        <small className="text-muted">ID: {card.cardId}</small>
                                    </td>
                                    <td>{card.beneficiaryName}</td>
                                    <td className="text-end fw-bold">{card.totalAmount.toLocaleString()} XOF</td>
                                    <td className="text-end">{card.paymentCount}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    render() {
        const { loading, error } = this.state;

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h5 className="mb-1">
                            <i className="fa fa-chart-bar me-2" />
                            Rapports de paiement
                        </h5>
                        <p className="text-muted mb-0">Statistiques et analyses des paiements</p>
                    </div>
                </div>

                {error && (
                    <Alert variant="danger" className="mb-4">
                        <i className="fa fa-exclamation-triangle me-2" />
                        {error}
                    </Alert>
                )}

                {this.renderFilters()}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </Spinner>
                        <div className="mt-3">Chargement des données...</div>
                    </div>
                ) : (
                    <>
                        {this.renderSummaryCards()}
                        <Row>
                            <Col xl={8}>
                                {this.renderPeriodChart()}
                            </Col>
                            <Col xl={4}>
                                {this.renderStatusTable()}
                            </Col>
                        </Row>
                        {this.renderProviderChart()}
                        {this.renderTopCards()}
                    </>
                )}
            </div>
        );
    }
}

export default PaymentReports;